import React from 'react'
import './note-area-fall-2023.css'
import NoteFall2023 from './NoteFall2023/NoteFall2023'
import { getNotes, createNote, updateNote, deleteNote, getStatusHistoryLogsForFamily } from '../cardModalFall2023UtilFunctions'
import CreateNoteModal2023 from './CreateNoteModal2023/CreateNoteModal2023'
import HistoryLogFall2023 from './HistoryLogFall2023/HistoryLogFall2023'

const buildHistoryGroupKey = (log) => {
    const rawDate = log.effectiveDate || log.date
    const date = rawDate ? new Date(rawDate) : null
    const secondBucket = date && !Number.isNaN(date.getTime())
        ? Math.floor(date.getTime() / 1000)
        : 'unknown-date'

    return [
        log.familyId || 'unknown-family',
        log.status || '',
        log.previousStatus || '',
        log.reason || '',
        log.note || '',
        secondBucket,
    ].join('|')
}

const collapseRedundantFamilyHistoryLogs = (logs, familyStudentCount) => {
    if (!familyStudentCount || familyStudentCount < 1) {
        return logs
    }

    const groupedLogs = logs.reduce((acc, log) => {
        const key = buildHistoryGroupKey(log)

        if (!acc.has(key)) {
            acc.set(key, [])
        }

        acc.get(key).push(log)
        return acc
    }, new Map())

    const hiddenLogIds = new Set()

    groupedLogs.forEach((group) => {
        const familyLog = group.find((log) => log.entityType === 'family')
        const studentLogs = group.filter((log) => log.entityType === 'student')

        if (familyLog && studentLogs.length >= familyStudentCount) {
            studentLogs.forEach((log) => {
                hiddenLogIds.add(log._id || log.id || `${log.familyId}-${log.studentId}-${log.effectiveDate || log.date}`)
            })
        }
    })

    return logs.filter((log) => {
        const logId = log._id || log.id || `${log.familyId}-${log.studentId}-${log.effectiveDate || log.date}`
        return !hiddenLogIds.has(logId)
    })
}

const NoteAreaFall2023 = ({userData, modalInfo, school, familyStudentCount = null}) => {
    const [sideBarNotes, setSideBarNotes] = React.useState([])
    const [statusHistoryLogs, setStatusHistoryLogs] = React.useState([])
    const [createNoteModal, setCreateNoteModal] = React.useState(false)
    const [editNote, setEditNote] = React.useState(false)
    
    React.useEffect(() => {
        makeInitCalls();
    }, [modalInfo.id, modalInfo.type])

    const timelineItems = React.useMemo(() => {
        const filteredHistoryLogs = collapseRedundantFamilyHistoryLogs(
            statusHistoryLogs,
            familyStudentCount
        )

        const noteItems = sideBarNotes.map((note) => ({
            id: note.id,
            entryType: 'note',
            sortDate: note.date,
            payload: note,
        }))

        const historyItems = filteredHistoryLogs.map((log, index) => ({
            id: `${log._id || log.id || `${log.familyId}-${log.studentId || 'family'}-${index}`}`,
            entryType: 'history',
            sortDate: log.effectiveDate || log.date,
            payload: log,
        }))

        return [...noteItems, ...historyItems].sort(
            (a, b) => new Date(b.sortDate) - new Date(a.sortDate)
        )
    }, [familyStudentCount, sideBarNotes, statusHistoryLogs])

    const makeInitCalls = async () => {
        if(!modalInfo.id) return
        const notes = await getNotes(modalInfo.type, modalInfo.id)
        setSideBarNotes(notes || [])

        if (modalInfo.type === 'familyId') {
            const historyLogs = await getStatusHistoryLogsForFamily(modalInfo.id)
            setStatusHistoryLogs(historyLogs || [])
            return
        }

        setStatusHistoryLogs([])
    }

    const createAndAddNote = async (noteInfo) => {
        let newNote = await createNote(noteInfo)
        let noteArray = sideBarNotes
        noteArray.push(newNote)
        noteArray.sort((a, b) => new Date(b.date) - new Date(a.date));
        setSideBarNotes([...noteArray])
        setCreateNoteModal(false)
    }

    const updateAndReplaceNote = async (noteInfo) => {
        let updatedNote = await updateNote(noteInfo)
        let noteArray = sideBarNotes
        let index = noteArray.findIndex((note) => note.id === updatedNote.id)
        noteArray[index] = updatedNote
        setSideBarNotes([...noteArray])
        setEditNote(false)
    }

    const deleteAndRemoveNote = async (noteInfo) => {
        let deletedNote = await deleteNote(noteInfo)
        let noteArray = sideBarNotes
        let index = noteArray.findIndex((note) => note.id === deletedNote.id)
        noteArray.splice(index, 1)
        setSideBarNotes([...noteArray])
        setEditNote(false)
    }



    return(
        <div className='note-area-fall-2023__wrapper'>
            {(createNoteModal || editNote) && 
                <CreateNoteModal2023 
                    modalInfo={modalInfo}
                    userData={userData}
                    setCreateNoteModal={setCreateNoteModal}
                    createAndAddNote={createAndAddNote}
                    editNote={editNote}
                    setEditNote={setEditNote}
                    updateAndReplaceNote={updateAndReplaceNote}
                    deleteAndRemoveNote={deleteAndRemoveNote}
                    school={school}
                />
            }
            <div className='note-area-fall-2023__add-note-btn' onClick={() => setCreateNoteModal(true)}>+ Add Note</div>
            <div className='note-area-fall-2023__note-scrollable-area'>
                {timelineItems.map((item) => (
                    item.entryType === 'note' ? (
                        <NoteFall2023 
                            key={`note-${item.id}`} 
                            note={item.payload} 
                            userData={userData} 
                            setEditNote={setEditNote} 
                            updateAndReplaceNote={updateAndReplaceNote} 
                            deleteAndRemoveNote={deleteAndRemoveNote} 
                        />
                    ) : (
                        <HistoryLogFall2023
                            key={`history-${item.id}`}
                            log={item.payload}
                        />
                    )
                ))}
            </div>
        </div>
    )
}
export default NoteAreaFall2023
